/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  ListSubheader,
  makeStyles, Typography,
} from '@material-ui/core';
import FormatIndentDecreaseIcon from '@material-ui/icons/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@material-ui/icons/FormatIndentIncrease';
import HearingIcon from '@material-ui/icons/Hearing';
import { Skeleton } from '@material-ui/lab';
import PropTypes from 'prop-types';
import React, { createContext, useEffect, useState } from 'react';
import {
  Cpu as CpuIcon, PieChart as PieChartIcon,
  User as UserIcon
} from 'react-feather';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import Logo from 'src/components/Logo';
import { DEFAULT_AVATAR } from 'src/utils/test';
import FoldNavItem from './FoldNavItem';
import NavItem from './NavItem';
import { useTranslation } from 'react-i18next';

const getNavConfig = (t) => [
  {
    subheader: t('nav.reports'),
    items: [
      {
        title: t('nav.overview'),
        icon: PieChartIcon,
        href: '/app/user/dashboard'
      }
    ]
  },
  {
    subheader: t('nav.applications'),
    items: [
      {
        title: t('nav.customer'),
        icon: UserIcon,
        href: '/app/user/account',
        items: [
          {
            title: t('nav.basicInfo'),
            href: '/app/user/account'
          },
          {
            title: t('nav.acousticParams'),
            href: '/app/user/audiogram'
          }
        ]
      },
      {
        title: t('nav.deviceSelection'),
        icon: HearingIcon,
        href: '/app/devices/hearingInstrument',
        items: [
          {
            title: t('nav.hearingAidSelection'),
            href: '/app/devices/hearingInstrument'
          },
          {
            title: t('nav.hearingAidDetail'),
            href: '/app/devices/hearingInstrument-detail'
          }
        ]
      },
      {
        title: t('nav.fitting'),
        icon: CpuIcon,
        href: '/app/fitting',
        items: [
          {
            title: t('nav.basicAdjustment'),
            href: '/app/fitting/basic'
          },
          {
            title: t('nav.fineAdjustment'),
            href: '/app/fitting/fine'
          },
          {
            title: t('nav.equipmentOptions'),
            href: '/app/fitting/equipment-options'
          },
          {
            title: t('nav.feedbackTest'),
            href: '/app/fitting/feedback'
          }
        ]
      }
    ]
  }
];

function renderNavItems({ items, isFold, ...rest }) {
  return (
    <List disablePadding>
      {
        items.reduce(
          (acc, item) => isFold ? reduceFoldChildRoutes({ acc, item, ...rest }) : reduceChildRoutes({ acc, item, ...rest }),
          []
        )
      }
    </List>
  );
}

function reduceFoldChildRoutes({
  acc,
  item,
  depth = 0
}) {
  const key = item.title + depth;
  acc.push(
    <FoldNavItem
      depth={depth}
      href={item.href}
      icon={item.icon}
      key={key}
      info={item.info}
      title={item.title}
      items={item.items}
    />
  );
  return acc;
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth = 0
}) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={key}
        info={item.info}
        open={Boolean(open)}
        items={item.items}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={key}
        info={item.info}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles((theme) => {
  return {
    mobileDrawer: {
      width: (props) => (props.isFold ? 100 : 256),
      // top: 64,
      // height: 'calc(100% - 64px)'
    },
    desktopDrawer: {
      width: (props) => (props.isFold ? 100 : 256),
      top: 64,
      height: 'calc(100% - 64px)'
    },
    desktopDrawer2: {
      width: 200,
      top: 64,
      left: 100,
      height: 'calc(100% - 64px)'
    },
    avatar: {
      cursor: 'pointer',
      width: 64,
      height: 64
    },
    fold: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      alignItems: 'center'
    }
  };
}

);

export const NavBarContext = createContext({});
function NavBar({ openMobile, onMobileClose, isFold, onFold, onUnfold, showDrawerMenu, setShowDrawerMenu }) {
  const classes = useStyles({ isFold });
  const location = useLocation();
  const { patient, hearingDeviceSettingLeft, hearingDeviceSettingRight } = useSelector((state) => state.patient);
  const [navInfo, setNavInfo] = useState(null);
  const { t } = useTranslation();
  const navConfig = getNavConfig(t);

  useEffect(() => {
    if (!patient) return; // patient 不存在时直接跳过
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  if (!patient) {
    return <Skeleton />;
  }
  
  const handleFoldToggle = () => {
    isFold ? onUnfold() : onFold();
  };

  const toggleDrawerMenu = (items) => {
    if (showDrawerMenu.show && items === showDrawerMenu.items) {
      setShowDrawerMenu({ show: false });
    }
    else {
      setShowDrawerMenu({ show: true, items });
    }
  };

  const content = (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box
            p={2}
            display="flex"
            justifyContent="center"
          >
            <RouterLink to="/app/user/dashboard">
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        {isFold && (
          <Box
            mt={2}
            mb={2}
            display="flex"
            justifyContent='center'
            alignItems='center'
            flexDirection='column'
          >
            <RouterLink to="/app/user/dashboard">
              <Avatar
                alt="User"
                className={classes.avatar}
                src={patient.avatar || DEFAULT_AVATAR}
              />
            </RouterLink>
            <Box pt={1}>
              <Link
                component={RouterLink}
                to="/app/account"
                variant="h5"
                color="textPrimary"
                underline="none"
              >
                {t('profile.nameFormat', {
                  firstName: patient.firstName,
                  lastName: patient.lastName
                })}
              </Link>
            </Box>

          </Box>
        )}
        {!isFold && (
          <Box p={2}>
            <Box
              display="flex"
              justifyContent="center"
            >
              <RouterLink to="/app/user/dashboard">
                <Avatar
                  alt="User"
                  className={classes.avatar}
                  src={patient.avatar}
                />
              </RouterLink>
            </Box>
            <Box
              mt={2}
              textAlign="center"
            >
              <Link
                component={RouterLink}
                to="/app/user/dashboard"
                variant="h5"
                color="textPrimary"
                underline="none"
              >

              {t('profile.nameFormat', {
                  firstName: patient.firstName,
                  lastName: patient.lastName
              })}
              </Link>
              <Typography
                variant="body2"
                color="textSecondary"
              >
                {patient.address}
              </Typography>
            </Box>
          </Box>
        )}
        <Divider />
        <Box p={2}>
          {navConfig.map((config) => {
            let items = config.items;
            if (config.subheader === "应用" && !hearingDeviceSettingLeft && !hearingDeviceSettingRight) {
              items = config.items.filter(item => item.href !== "/app/fitting");

            }
            return <List
              key={config.subheader}
              subheader={(
                <ListSubheader
                  disableGutters
                  disableSticky
                >
                  {config.subheader}
                </ListSubheader>
              )}
            >
              {renderNavItems({ items: items, pathname: location.pathname, isFold })}
            </List>;
          })}
        </Box>
        <Divider />
        {
          !false && (
            <Box p={2}>
              <Box
                p={2}
                borderRadius="borderRadius"
                bgcolor="background.dark"
              >
                <Typography
                  variant="h6"
                  color="textPrimary"
                >
                   {t('nav.needHelp')}
                </Typography>
                {/* <Link
                    variant="subtitle1"
                    color="secondary"
                    component={RouterLink}
                    to="/docs"
                  >
                    请联系我们！
              </Link> */}
              </Box>
            </Box>
          )
        }

      </PerfectScrollbar>
      <Divider />
      <Box className={classes.fold} p={1} pl={2} onClick={handleFoldToggle}>
        {isFold ? <FormatIndentIncreaseIcon /> : <FormatIndentDecreaseIcon />}
      </Box>
    </Box >
  );



  return (
    <NavBarContext.Provider value={{ navInfo, setNavInfo, toggleDrawerMenu }}>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>

      {
        isFold && showDrawerMenu.show && <Hidden mdDown><Drawer anchor="left"
          classes={{ paper: classes.desktopDrawer2 }}
          open
          variant="persistent">
          {
            renderNavItems({ items: showDrawerMenu.items, isFold: false })
          }
        </Drawer></Hidden>
      }
    </NavBarContext.Provider>
  );
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  isFold: PropTypes.bool,
  onFold: PropTypes.func,
  onUnfold: PropTypes.func
};

export default NavBar;
